export interface StoredPageContent {
  components?: unknown;
  styles?: unknown;
  css?: string;
  html?: string;
}

export interface NormalizedEditorContent {
  components: string | unknown;
  css: string;
}

export interface GrapesEditorLike {
  setComponents: (input: unknown, ...args: unknown[]) => unknown;
  setStyle: (input: unknown, ...args: unknown[]) => unknown;
}

const HTML_START = '<!-- HTML_START -->';
const HTML_END = '<!-- HTML_END -->';
const CSS_START = '<!-- CSS_START -->';
const CSS_END = '<!-- CSS_END -->';

/**
 * Parse stored page content from API / DB shape.
 */
export function normalizeStoredContent(raw: unknown): StoredPageContent {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return normalizeStoredContent(JSON.parse(raw));
    } catch {
      return { components: raw };
    }
  }
  if (typeof raw === 'object') {
    return raw as StoredPageContent;
  }
  return {};
}

function extractBetween(raw: string, startMarker: string, endMarker: string): string | null {
  const start = raw.indexOf(startMarker);
  const end = raw.indexOf(endMarker);
  if (start === -1 || end === -1 || end <= start) {
    return null;
  }
  return raw.slice(start + startMarker.length, end).trim();
}

/**
 * Normalize AI / wizard HTML into body markup + optional CSS.
 */
export function normalizeAiWebsiteHtml(rawHtml: string, rawCss = ''): { html: string; css: string } {
  let html = rawHtml.trim();
  let css = rawCss.trim();

  const markedHtml = extractBetween(html, HTML_START, HTML_END);
  if (markedHtml) html = markedHtml;

  const markedCss = extractBetween(rawHtml, CSS_START, CSS_END);
  if (markedCss) css = markedCss;

  const fencedHtml = rawHtml.match(/```html\n([\s\S]*?)```/i)?.[1]?.trim();
  if (fencedHtml) html = fencedHtml;

  const fencedCss = rawHtml.match(/```css\n([\s\S]*?)```/i)?.[1]?.trim();
  if (fencedCss) css = fencedCss;

  const embedded = extractEmbeddedCss(html);
  html = embedded.html;
  css = [css, embedded.css].filter(Boolean).join('\n').trim();

  html = extractBodyHtml(html);
  return { html, css };
}

export function extractBodyHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed.startsWith('<!DOCTYPE') && !trimmed.startsWith('<html')) {
    return trimmed;
  }
  const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    return bodyMatch[1].trim();
  }
  return trimmed
    .replace(/<!DOCTYPE[^>]*>/i, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/i, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .trim();
}

export function extractEmbeddedCss(html: string): { html: string; css: string } {
  let css = '';
  const withoutStyles = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_match, styleContent: string) => {
    css += `${styleContent}\n`;
    return '';
  });
  return { html: withoutStyles.trim(), css: css.trim() };
}

function hasGrapesStyleRules(styles: unknown): boolean {
  if (!Array.isArray(styles) || styles.length === 0) return false;
  return styles.some((rule) => {
    if (!rule || typeof rule !== 'object') return false;
    const record = rule as Record<string, unknown>;
    if (Array.isArray(record.selectors) && record.selectors.length > 0) return true;
    if (record.style && typeof record.style === 'object' && Object.keys(record.style as object).length > 0) {
      return true;
    }
    return false;
  });
}

export function contentForEditorApply(raw: unknown): NormalizedEditorContent | null {
  const stored = normalizeStoredContent(raw);
  const componentSource = stored.components ?? stored.html;
  if (!componentSource) return null;

  let components: string | unknown = componentSource;
  let css = stored.css ? String(stored.css) : '';

  if (typeof componentSource === 'string') {
    const normalized = normalizeAiWebsiteHtml(componentSource, css);
    components = normalized.html;
    css = normalized.css || css;
  }

  return { components, css };
}

export function applyContentToEditor(editor: unknown, raw: unknown): boolean {
  const prepared = contentForEditorApply(raw);
  if (!prepared || !editor || typeof editor !== 'object') return false;

  const grapes = editor as GrapesEditorLike;
  grapes.setComponents(prepared.components);

  const stored = normalizeStoredContent(raw);
  if (hasGrapesStyleRules(stored.styles)) {
    grapes.setStyle(stored.styles);
  } else if (prepared.css.trim()) {
    grapes.setStyle(prepared.css);
  }

  return true;
}
